import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useWorkItemStore } from '../store/useWorkItemStore';
import { useAppStore } from '../store/useAppStore';
import { useProjectStore } from '../store/useProjectStore';
import { useOrgHierarchyStore } from '../store/useOrgHierarchyStore';
import { usePIStore } from '../store/usePIStore';
import { useDashboardConfigStore } from '../store/useDashboardConfigStore';
import KanbanBoard from '../components/kanban/KanbanBoard';
import SprintPlanningBoard from '../components/kanban/SprintPlanningBoard';
import TaskListView from '../components/tasks/TaskListView';
import { ChevronDown, LayoutGrid, CalendarRange, List, Loader2 } from 'lucide-react';
import RefreshButton from '../components/common/RefreshButton';

type BoardView = 'task-progress' | 'sprint-planning' | 'list';

// How often the board silently re-pulls the current scope (ms). Cheap now that
// the board fetch uses the gzipped light projection (~38 KB).
const POLL_INTERVAL_MS = 30_000;

/** `projectId` scopes the board to one project wherever it is embedded (e.g. a training programme). */
export default function KanbanPage({ projectId: scoped }: { projectId?: string } = {}) {
  const params = useParams<{ projectId: string }>();
  const projectId = scoped || params.projectId;
  const { setSelectedProject } = useAppStore();
  const { items, fetchByProjects, loading, refreshing } = useWorkItemStore();
  const { projects } = useProjectStore();
  const {
    programs,
    releaseTrains,
    fetchPrograms,
    fetchReleaseTrains,
  } = useOrgHierarchyStore();

  const { fetchPIs, fetchAllSprints } = usePIStore();
  const { config: dashConfig, fetchConfig: fetchDashConfig, recordProjectAccess } =
    useDashboardConfigStore();

  // View toggle
  const [boardView, setBoardView] = useState<BoardView>('task-progress');

  // Filter state
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null);
  const [selectedReleaseTrainId, setSelectedReleaseTrainId] = useState<string | null>(null);
  const [selectedProjectIdLocal, setSelectedProjectIdLocal] = useState<string | null>(projectId || null);

  // Load hierarchy data on mount
  useEffect(() => {
    fetchPrograms();
    fetchReleaseTrains();
    fetchPIs();
    fetchAllSprints();
    fetchDashConfig();          // supplies the remembered scope
  }, [fetchPrograms, fetchReleaseTrains, fetchPIs, fetchAllSprints, fetchDashConfig]);

  // If navigated with a projectId param, select that project and infer hierarchy
  useEffect(() => {
    if (projectId) {
      setSelectedProjectIdLocal(projectId);
      const proj = projects.find((p) => p._id === projectId);
      if (proj) {
        setSelectedProgramId(proj.programId || null);
        setSelectedReleaseTrainId(proj.releaseTrainId || null);
      }
    }
  }, [projectId, projects]);

  // Release trains filtered by selected program
  const filteredReleaseTrains = useMemo(() => {
    if (!selectedProgramId) return releaseTrains;
    return releaseTrains.filter((rt) => rt.programId === selectedProgramId);
  }, [releaseTrains, selectedProgramId]);

  // Projects filtered by selected hierarchy
  const filteredProjects = useMemo(() => {
    let filtered = projects;
    if (selectedProgramId) {
      filtered = filtered.filter((p) => p.programId === selectedProgramId);
    }
    if (selectedReleaseTrainId) {
      filtered = filtered.filter((p) => p.releaseTrainId === selectedReleaseTrainId);
    }
    return filtered;
  }, [projects, selectedProgramId, selectedReleaseTrainId]);

  // Which project IDs to fetch work items for.
  //
  // BG-194: this used to fall back to *every* project, which fetched 18,891
  // work items across 62 projects in 16 seconds because nobody had picked one.
  // Never load documents for a scope the user did not ask for — fall back to
  // where they last worked, and failing that show a picker.
  const activeProjectIds = useMemo(() => {
    if (selectedProjectIdLocal) return [selectedProjectIdLocal];

    // A narrowing filter is an explicit scope, so honour it.
    const narrowed = Boolean(selectedProgramId || selectedReleaseTrainId);
    if (narrowed) return filteredProjects.map((p) => p._id);

    const remembered = dashConfig?.lastProjectId;
    if (remembered && filteredProjects.some((p) => p._id === remembered)) {
      return [remembered];
    }
    return [];
  }, [selectedProjectIdLocal, filteredProjects, selectedProgramId,
      selectedReleaseTrainId, dashConfig?.lastProjectId]);


  // Fetch work items when active project IDs change
  useEffect(() => {
    if (activeProjectIds.length > 0) {
      fetchByProjects(activeProjectIds, 'board');
      if (activeProjectIds.length === 1) {
        setSelectedProject(activeProjectIds[0]);
        // Deduped in the store and debounced again server-side, so this is safe
        // to call on every scope change.
        recordProjectAccess(activeProjectIds[0]);
      }
    }
  }, [activeProjectIds, fetchByProjects, setSelectedProject, recordProjectAccess]);

  // Auto-refresh: silently re-pull the current scope on an interval so records
  // added elsewhere appear without a manual refresh. Uses the light board
  // projection + the stale-while-revalidate path (no blank flash). Pauses while
  // the tab is backgrounded and catches up immediately on return.
  useEffect(() => {
    if (activeProjectIds.length === 0) return;

    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      // Don't stack a poll on top of an in-flight fetch.
      const { loading, refreshing } = useWorkItemStore.getState();
      if (loading || refreshing) return;
      fetchByProjects(activeProjectIds, 'board');
    };

    let timer = window.setInterval(tick, POLL_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        tick(); // catch up on whatever changed while hidden
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [activeProjectIds, fetchByProjects]);

  // Clear child selections when parent changes
  const handleProgramChange = (programId: string | null) => {
    setSelectedProgramId(programId);
    setSelectedReleaseTrainId(null);
    setSelectedProjectIdLocal(null);
  };

  const handleReleaseTrainChange = (rtId: string | null) => {
    setSelectedReleaseTrainId(rtId);
    setSelectedProjectIdLocal(null);
  };

  const handleProjectChange = (projId: string | null) => {
    setSelectedProjectIdLocal(projId);
    if (projId) setSelectedProject(projId);
  };

  const hasNoSelection = activeProjectIds.length === 0;

  /** Recently opened projects the user can still see, for the empty state. */
  const recentPickable = useMemo(() => {
    const recent = dashConfig?.recentProjectIds || [];
    return recent
      .map((id) => filteredProjects.find((p) => p._id === id))
      .filter((p): p is NonNullable<typeof p> => Boolean(p))
      .slice(0, 6);
  }, [dashConfig?.recentProjectIds, filteredProjects]);

  return (
    <div className="flex flex-col h-full">
      {/* Hierarchy Filter Bar */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shrink-0">
        <LayoutGrid size={16} className="text-accent shrink-0" />
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 shrink-0">Board</span>

        {/* View Toggle */}
        <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden ml-2">
          <button
            onClick={() => setBoardView('task-progress')}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium transition-colors ${
              boardView === 'task-progress'
                ? 'bg-accent text-white'
                : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
          >
            <LayoutGrid size={12} />
            Task Progress
          </button>
          <button
            onClick={() => setBoardView('sprint-planning')}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium transition-colors ${
              boardView === 'sprint-planning'
                ? 'bg-accent text-white'
                : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
          >
            <CalendarRange size={12} />
            Sprint Planning
          </button>
          <button
            onClick={() => setBoardView('list')}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium transition-colors ${
              boardView === 'list'
                ? 'bg-accent text-white'
                : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
          >
            <List size={12} />
            List
          </button>
        </div>

        <div className="flex items-center gap-2 ml-4">
          <FilterDropdown
            label="Program"
            value={selectedProgramId}
            onChange={handleProgramChange}
            options={programs.map((p) => ({ id: p._id, name: p.name }))}
            allLabel="All Programs"
          />
          <FilterDropdown
            label="Release Train"
            value={selectedReleaseTrainId}
            onChange={handleReleaseTrainChange}
            options={filteredReleaseTrains.map((rt) => ({ id: rt._id, name: rt.name }))}
            allLabel="All Release Trains"
          />
          <FilterDropdown
            label="Project"
            value={selectedProjectIdLocal}
            onChange={handleProjectChange}
            options={filteredProjects.map((p) => ({ id: p._id, name: p.name }))}
            allLabel="All Projects"
          />
        </div>

        <div className="ml-auto flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
          {loading ? (
            <span className="flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Loading…</span>
          ) : (
            <span className="flex items-center gap-1">
              {refreshing && <Loader2 size={12} className="animate-spin" />}
              {items.length} items across {activeProjectIds.length} project{activeProjectIds.length !== 1 ? 's' : ''}
              {refreshing && <span className="text-gray-400 dark:text-gray-500"> · refreshing…</span>}
            </span>
          )}
          <RefreshButton
            onRefresh={async () => {
              if (activeProjectIds.length > 0) await fetchByProjects(activeProjectIds, 'board');
            }}
            tooltip="Refresh board data"
          />
        </div>
      </div>

      {hasNoSelection ? (
        <div className="flex flex-col items-center justify-center flex-1 text-gray-400 dark:text-gray-500 gap-4 px-6">
          <LayoutGrid size={48} />
          <div className="text-center">
            <p className="text-lg">Pick a project to view the board</p>
            <p className="text-sm mt-1">
              Boards load per project, so nothing is fetched until you choose one.
            </p>
          </div>
          {/* Straight to the projects actually worked on, rather than making
              someone hunt through a filter for the one they had open. */}
          {recentPickable.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-2 max-w-2xl">
              {recentPickable.map((p) => (
                <button
                  key={p._id}
                  onClick={() => setSelectedProjectIdLocal(p._id)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-accent hover:text-accent"
                >
                  {p.displayId && (
                    <span className="font-mono text-xs text-gray-400 mr-1.5">{p.displayId}</span>
                  )}
                  {p.name}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : boardView === 'sprint-planning' ? (
        <SprintPlanningBoard />
      ) : boardView === 'list' ? (
        <TaskListView />
      ) : (
        <KanbanBoard projectId={selectedProjectIdLocal || undefined} />
      )}
    </div>
  );
}

// ─── Reusable filter dropdown ──────────────────────────────────────
interface FilterDropdownProps {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  options: { id: string; name: string }[];
  allLabel: string;
}

function FilterDropdown({ label, value, onChange, options, allLabel }: FilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.id === value);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm hover:border-accent/50 transition-colors min-w-[140px]"
      >
        <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-semibold">{label}:</span>
        <span className="text-gray-700 dark:text-gray-200 truncate max-w-[150px]">
          {selected ? selected.name : allLabel}
        </span>
        <ChevronDown size={14} className="text-gray-400 ml-auto shrink-0" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-40 max-h-64 overflow-y-auto">
            <button
              onClick={() => { onChange(null); setOpen(false); }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                !value ? 'text-accent font-semibold bg-accent/5' : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              {allLabel}
            </button>
            {options.map((opt) => (
              <button
                key={opt.id}
                onClick={() => { onChange(opt.id); setOpen(false); }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                  value === opt.id ? 'text-accent font-semibold bg-accent/5' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {opt.name}
              </button>
            ))}
            {options.length === 0 && (
              <p className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500 italic">No items available</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
